import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiLock, FiUser, FiEye, FiEyeOff } from 'react-icons/fi';
import { FiShield } from 'react-icons/fi';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate a network request
    setTimeout(() => {
      // For demo purposes, we'll just use a simple check
      if (email === 'admin@matrixfinance.com' && password === 'admin123') {
        navigate('/admin');
      } else {
        setError('Invalid email or password');
        setIsLoading(false);
      }
    }, 1000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background overflow-hidden relative">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-20%] w-[70%] h-[70%] rounded-full bg-gradient-to-r from-primary/20 to-purple-500/10 blur-[120px]" />
        <div className="absolute bottom-[-40%] right-[-20%] w-[80%] h-[70%] rounded-full bg-gradient-to-l from-primary/20 to-blue-500/10 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-[0.03]" />
      </div>
      
      {/* Animated shapes */}
      <motion.div 
        className="absolute top-1/4 left-1/4 w-12 h-12 rounded-full bg-gradient-to-r from-primary/20 to-transparent backdrop-blur-sm border border-primary/20"
        animate={{
          y: [0, -50, 0],
          x: [0, 30, 0],
          rotate: [0, 180, 360]
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      
      <motion.div 
        className="absolute bottom-1/4 right-1/3 w-20 h-20 rounded-xl bg-gradient-to-r from-blue-500/10 to-transparent backdrop-blur-sm border border-blue-500/20"
        animate={{
          y: [0, 60, 0],
          x: [0, -40, 0],
          rotate: [0, -180, -360]
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      
      <motion.div 
        className="absolute top-2/3 right-1/4 w-16 h-16 rounded-lg bg-gradient-to-r from-purple-500/10 to-transparent backdrop-blur-sm border border-purple-500/20"
        animate={{
          y: [0, -40, 0],
          x: [0, -20, 0],
          rotate: [0, 120, 240, 360]
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="p-8 bg-foreground/80 backdrop-blur-md rounded-2xl shadow-md border border-border">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="text-center mb-8"
          >
            <div className="h-20 w-20 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 shadow-glow-sm">
              M
            </div>
            <h1 className="text-2xl font-bold">Matrix Finance</h1>
            <p className="text-text/70 mt-1">Sign in to access the admin dashboard</p>
          </motion.div>
          
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-100/80 backdrop-blur-sm border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6"
            >
              <div className="flex items-center">
                <FiShield className="mr-2" />
                <span>{error}</span>
              </div>
            </motion.div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label htmlFor="email" className="block text-sm font-medium mb-2">Email</label>
              <div className="relative">
                <motion.input
                  whileFocus={{ boxShadow: "0 0 0 2px rgba(67, 97, 238, 0.3)" }}
                  id="email"
                  type="email"
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-lg bg-card/50 backdrop-blur-sm focus:outline-none focus:border-primary transition-all duration-300"
                  placeholder="admin@matrixfinance.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <div className="absolute left-3 top-3.5 text-text/50">
                  <FiUser size={18} />
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <motion.input
                  whileFocus={{ boxShadow: "0 0 0 2px rgba(67, 97, 238, 0.3)" }}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="w-full pl-10 pr-12 py-3 border border-border rounded-lg bg-card/50 backdrop-blur-sm focus:outline-none focus:border-primary transition-all duration-300"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <div className="absolute left-3 top-3.5 text-text/50">
                  <FiLock size={18} />
                </div>
                <button
                  type="button"
                  className="absolute right-3 top-3.5 text-text/50 hover:text-text transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>
            
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-text/70">
                  Remember me
                </label>
              </div>
              
              <div className="text-sm">
                <a href="#" className="text-primary hover:text-primary/80 transition-colors">
                  Forgot password?
                </a>
              </div>
            </div>
            
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isLoading}
              className={`w-full relative bg-primary text-white py-3 px-4 rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-all duration-300 overflow-hidden ${isLoading ? 'cursor-not-allowed opacity-80' : ''}`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                "Sign in"
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-primary opacity-0 hover:opacity-100 transition-opacity duration-300" />
            </motion.button>
          </form>
          
          <div className="mt-8 text-center text-sm text-text/70">
            <p>Need help? <a href="#" className="text-primary hover:text-primary/80 transition-colors">Contact system administrator</a></p>
          </div>
        </div>
        
        <div className="absolute -bottom-6 -left-6 -right-6 h-12 bg-foreground/50 backdrop-blur-sm rounded-b-3xl z-[-1] transform skew-x-[5deg]"></div>
      </motion.div>
    </div>
  );
};

export default AdminLogin; 